import React, { useEffect, useState } from 'react'
import { useUnifiedGlobalFormPresetInputParameters } from '../../hooks'
import { InputParameterConfigForm } from '../../../../../../components/InputParameterHierarchy/InputParameterConfigForm'
import { YesNoCancelModal } from '../../../../../../components/Modal/YesNoCancelModal'
import { Button } from '../../../../../../components/Button/Button'
import { AiFeaturePreset } from '../../../../../../models/AiFeaturePreset'
import { PresetConfigLoader } from '../../Common/PresetConfigLoader'
import { useInputParametersUpdateScope } from '../../../../../../components/InputParameterHierarchy/hooks'
import { MeetingTemplateInputParametersHierarchyConfigComponentProps } from '../../../../../../components/InputParameterHierarchy/model'
import { useAppSelector } from '../../../../../../store/hooks'
import { selectAccountId } from '../../../../../../store/auth/authSlice'
import { useGetPresetByIdQuery } from '../../../../../../store/ai/ai'

export const GlobalPresetConfigForm: React.FC<MeetingTemplateInputParametersHierarchyConfigComponentProps> = ({ typeId, onHide, lockedByEnterprise }) => {
  const accountId = useAppSelector(selectAccountId)

  const { data: presetData } = useGetPresetByIdQuery({ accountId, presetId: typeId })

  const { presetInputParameters, updateInputParameters, updateLoading } = useUnifiedGlobalFormPresetInputParameters(presetData?.data)

  if (!presetData || !presetData.data || !presetInputParameters) {
    return <PresetConfigLoader />
  }

  return (
    <GlobalPresetConfigFormBody
      preset={presetData?.data}
      inputParameters={presetInputParameters}
      onSave={updateInputParameters}
      onHide={onHide}
      updateLoading={updateLoading}
      lockedByEnterprise={lockedByEnterprise}
    />
  )
}

interface GlobalPresetConfigFormBodyProps {
  preset: AiFeaturePreset
  inputParameters: { [key: string]: any }
  onSave: (inputParameters: { [key: string]: any }) => Promise<void>
  onHide: () => void
  updateLoading: boolean
  lockedByEnterprise?: boolean
}

const GlobalPresetConfigFormBody: React.FC<GlobalPresetConfigFormBodyProps> = ({
  preset,
  inputParameters,
  onSave,
  onHide,
  updateLoading,
  lockedByEnterprise = false,
}) => {
  const { hasAiFeatureUpdateScope } = useInputParametersUpdateScope()

  const [showConfirmation, setShowConfirmation] = useState(false)
  const [displayedInputParameters, setDisplayedInputParameters] = useState<{ [key: string]: any }>(inputParameters)
  const [isChanged, setChanged] = useState<boolean>(false)

  useEffect(() => {
    setDisplayedInputParameters(inputParameters)
  }, [inputParameters])

  const handleClickClose = () => {
    if (isChanged) {
      setShowConfirmation(true)
    } else {
      onHide()
    }
  }

  const handleClickCancel = () => {
    setDisplayedInputParameters(inputParameters)
  }

  const handleClickSave = async () => {
    setChanged(false)
    await onSave(displayedInputParameters)
  }

  useEffect(() => {
    setChanged(JSON.stringify(displayedInputParameters) !== JSON.stringify(inputParameters))
  }, [displayedInputParameters, inputParameters])

  return (
    <>
      <YesNoCancelModal
        show={showConfirmation}
        onHide={() => setShowConfirmation(false)}
        message={'Do you want to save your changes?'}
        onYes={() => {
          onSave(displayedInputParameters)
          onHide()
        }}
        onNo={() => {
          setDisplayedInputParameters(inputParameters)
          onHide()
        }}
      />
      <InputParameterConfigForm
        inputParametersSchema={preset.inputParametersSchema}
        inputParameters={displayedInputParameters}
        showCustomizationSetting={false}
        customized={false}
        onChange={setDisplayedInputParameters}
        disabled={!hasAiFeatureUpdateScope || lockedByEnterprise}
      />
      <div className='w-100 d-flex justify-content-between zocks-mt-3'>
        <Button variant='borderless' onClick={handleClickClose}>
          Close
        </Button>
        {hasAiFeatureUpdateScope && !lockedByEnterprise && (
          <div className='d-flex gap-2'>
            <Button variant='secondary' onClick={handleClickCancel} disabled={!isChanged} loading={updateLoading}>
              Cancel
            </Button>
            <Button variant='primary' onClick={handleClickSave} disabled={!isChanged} loading={updateLoading}>
              Save
            </Button>
          </div>
        )}
      </div>
    </>
  )
}
